import React, {
    Dispatch,
    forwardRef,
    SetStateAction,
    useEffect,
    useState,
} from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import { LoadingSpinner } from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { addDirectedTreeIcons, summarizeDirectedTree } from '@trqk/utils';
import { StatementsChartsContent } from './StatementsChartsContent';
import { summarizeDirectedTreeConfig } from './constants';
import { hideExplorerSection, showExplorerSection } from './helpers';
import { getFeatureFlags } from '../../../../../featureFlags';
import { useAnchorLinks } from '../../../../../hooks';

const { GET_MERGED_STATEMENT_VIEWS, GET_UNVIEWED_STATEMENTS } = queryStrings;

interface StatementsChartsProps {
    selectedIdsString: string;
    setSectionConfigs: Dispatch<SetStateAction<HeaderSectionConfig[]>>;
    setShowMediaExplorer: SetState<boolean>;
}

/**
 * Displays a visualization of one or more PRO statements, as a series of charts. This component
 * only fetches the merged dashboard data; the StatementsChartsContent component renders the content
 * of the page.
 */
export const StatementsCharts = forwardRef(
    (props: StatementsChartsProps, ref): JSX.Element | null => {
        const { truncateChartData } = getFeatureFlags();
        const { selectedIdsString, setSectionConfigs, setShowMediaExplorer } =
            props;
        const [loadingMediaExplorerData, setLoadingMediaExplorerData] =
            useState(true);
        const [mediaExplorerData, setMediaExplorerData] = useState<
            ProDirectedTreeNode[] | null
        >(null);
        const [getMergedStatementViews, getMergedStatementViewsQuery] =
            useLazyQuery(GET_MERGED_STATEMENT_VIEWS, {
                onCompleted: data => {
                    const directedTree =
                        data?.getMergedStatementViews?.view?.directed_tree ??
                        null;
                    const nextMediaExplorerData = truncateChartData
                        ? summarizeDirectedTree({
                              directedTree,
                              config: summarizeDirectedTreeConfig,
                          })
                        : addDirectedTreeIcons({
                              directedTree,
                              config: summarizeDirectedTreeConfig,
                          });

                    if (nextMediaExplorerData === null) {
                        setSectionConfigs(
                            (
                                prev: HeaderSectionConfig[],
                            ): HeaderSectionConfig[] =>
                                prev.map(hideExplorerSection),
                        );
                    } else {
                        setSectionConfigs(
                            (
                                prev: HeaderSectionConfig[],
                            ): HeaderSectionConfig[] =>
                                prev.map(showExplorerSection),
                        );
                    }

                    setMediaExplorerData(nextMediaExplorerData);
                    setLoadingMediaExplorerData(false);
                },
            });

        const { cache } = useApolloClient();

        useEffect(() => {
            const selectedIds: string[] = JSON.parse(selectedIdsString);

            if (selectedIds.length > 0) {
                setLoadingMediaExplorerData(true);
                getMergedStatementViews({
                    variables: {
                        ids: selectedIds.sort(),
                    },
                });

                const queryData = cache.readQuery({
                    query: GET_UNVIEWED_STATEMENTS,
                });

                // I'm ignoring a TSC error here that is caused by the cache not being typed. It
                // might take a lot of work to type the cache at this point.
                const prev: string[] =
                    // eslint-disable-next-line
                    // @ts-ignore
                    queryData?.uiState?.statements?.unviewedStatements ?? [];

                cache.writeQuery({
                    query: GET_UNVIEWED_STATEMENTS,
                    data: {
                        uiState: {
                            __typename: 'UIState',
                            statements: {
                                __typename: 'Statements',
                                unviewedStatements: prev.filter(
                                    (unviewedId: string): boolean =>
                                        !selectedIds.includes(unviewedId),
                                ),
                            },
                        },
                    },
                });
            }
        }, [cache, getMergedStatementViews, selectedIdsString]);

        const mergedStatementView =
            getMergedStatementViewsQuery?.data?.getMergedStatementViews ?? null;
        const dashboard = mergedStatementView?.view ?? null;
        const mergedId = mergedStatementView?.id ?? '__no_id__';
        const chartParameters: ProChartParameter[] =
            dashboard?.chart_parameters ?? [];
        // If truncateChartData is true, the loadingMediaExplorerData / mediaExplorerData props will
        // determine whether to render the Media Explorer.
        // If truncateChartData is false, dashboard.chart_parameters determines whether to render
        // the Media Explorer.
        const showMediaExplorer = truncateChartData
            ? mediaExplorerData !== null
            : chartParameters?.some(
                  ({ chart_name, show }: ProChartParameter): boolean =>
                      chart_name === 'force_directed_tree' && show === true,
              );

        useEffect(() => {
            setShowMediaExplorer(showMediaExplorer);
        }, [showMediaExplorer]);

        const { setActiveScrollObservers } = useAnchorLinks();
        const { pathname } = useLocation();
        const showLoadingSpinner = dashboard === null;

        useEffect(() => {
            if (!showLoadingSpinner) {
                setActiveScrollObservers(
                    (
                        prev: Record<string, boolean>,
                    ): Record<string, boolean> => ({
                        ...prev,
                        [pathname]: false,
                    }),
                );
            }
        }, [pathname, showLoadingSpinner]);

        if (showLoadingSpinner) {
            return (
                <div
                    className="w-full flex items-center justify-center"
                    style={{ height: 600 }}
                >
                    <LoadingSpinner />
                </div>
            );
        }

        return (
            <StatementsChartsContent
                dashboard={dashboard}
                loadingMediaExplorerData={loadingMediaExplorerData}
                mediaExplorerData={mediaExplorerData}
                showMediaExplorer={showMediaExplorer}
                ref={ref}
                id={mergedId}
            />
        );
    },
);

StatementsCharts.displayName = 'StatementsCharts';
